import React, { useCallback, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDownIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
interface ContentAccordionSectionProps {
  title: string;
  icon?: React.ReactNode;
  itemCount: number;
  defaultOpen?: boolean;
  subtitle?: string;
  subtitleTooltip?: string;
  children: React.ReactNode;
}
const InfoTooltip: React.FC<{
  text: string;
  darkMode: boolean;
}> = ({
  text,
  darkMode
}) => {
  const dk = useDarkClasses();
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({
    top: 0,
    left: 0
  });
  const [flipLeft, setFlipLeft] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const show = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const spaceOnRight = window.innerWidth - rect.right;
      if (spaceOnRight < 240) {
        setCoords({
          top: rect.top - 8,
          left: rect.left - 8
        });
        setFlipLeft(true);
      } else {
        setCoords({
          top: rect.top - 8,
          left: rect.right + 8
        });
        setFlipLeft(false);
      }
    }
    setVisible(true);
  }, []);
  const hide = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setVisible(false);
  }, []);
  return <>
      <span ref={ref} onMouseEnter={show} onMouseLeave={hide} onClick={e => e.stopPropagation()} className={`flex items-center justify-center w-4 h-4 rounded-full text-xs font-bold cursor-default select-none ${dk.isDark ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-200 text-gray-500 hover:bg-gray-300'}`} data-id="element-3689" data-mp="12bgglit8g45o">
        i
      </span>
      {visible && ReactDOM.createPortal(<div style={{
      position: 'fixed',
      top: coords.top,
      left: coords.left,
      transform: flipLeft ? 'translate(-100%, -100%)' : 'translateY(-100%)',
      zIndex: 9999,
      maxWidth: '380px',
      width: 'max-content'
    }} className={`text-xs rounded-lg px-3 py-2 shadow-lg pointer-events-none ${dk.isDark ? 'bg-gray-700 text-gray-200 border border-gray-600' : 'bg-white text-gray-700 border border-gray-200'}`} data-id="element-3690" data-mp="2no1eemjuy1e7">
            {text}
          </div>, document.body)}
    </>;
};
export const ContentAccordionSection: React.FC<ContentAccordionSectionProps> = ({
  title,
  icon,
  itemCount,
  defaultOpen = false,
  subtitle,
  subtitleTooltip,
  children
}) => {
  const dk = useDarkClasses();
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return <div className={`rounded-xl border overflow-hidden transition-colors ${dk.card}`} data-id="element-3691" data-mp="3mcafq27hmptp">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className={`w-full flex items-center justify-between px-4 py-3.5 sm:px-5 sm:py-4 transition-colors ${dk.dropdownItemHover}`} data-id="element-3692" data-mp="3iaov1uopp78y">
        <div className="flex items-center gap-2.5" data-id="element-3693" data-mp="3cy0a1o1f8i13">
          {icon && <span className={`flex-shrink-0 ${dk.textMuted}`} data-id="element-3694" data-mp="2y8lyc9adhoij">{icon}</span>}
          <h3 className={`text-base sm:text-lg font-semibold ${dk.textHeading}`} data-id="element-3695" data-mp="2y6hn16a59n88">
            {title}
          </h3>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${dk.badgeNeutral}`} data-id="element-3696" data-mp="3qwq2urh7jvjn">
            {itemCount}
          </span>
        </div>
        <div className="flex items-center gap-2" data-id="element-3697" data-mp="3gz3oq77qg80q">
          {subtitle && <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()} data-id="element-3698" data-mp="3h16c0jhi3n1r">
              <span className={`hidden sm:inline text-xs font-medium ${dk.textMuted}`} data-id="element-3699" data-mp="25k6jpnv5l87q">
                {subtitle}
              </span>
              {subtitleTooltip && <InfoTooltip text={subtitleTooltip} darkMode={dk.isDark} data-id="element-3700" data-mp="2qqo1mda07o32" />}
            </div>}
          <ChevronDownIcon className={`h-5 w-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''} ${dk.textMuted}`} data-id="element-3701" data-mp="2lyeet646r450" />
        </div>
      </button>
      <div className={`transition-all duration-200 ease-in-out overflow-hidden ${isOpen ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-3702" data-mp="2c1rvilricl53">
        <div className={`px-4 pb-4 sm:px-5 sm:pb-5 border-t ${dk.isDark ? 'border-gray-700' : 'border-gray-100'}`} data-id="element-3703" data-mp="39mxlxibi1avi">
          <div className="pt-3 sm:pt-4" data-id="element-3704" data-mp="2zqb3f06navwn">{children}</div>
        </div>
      </div>
    </div>;
};
/**
 * Helper to sort grouped entries alphabetically by category label.
 * Pass in Object.entries(grouped) and optionally a label map.
 */
export function sortEntriesAlphabetically<T>(entries: [string, T[]][], labelMap?: Record<string, string>): [string, T[]][] {
  return [...entries].filter(([, items]) => items.length > 0).sort(([a], [b]) => {
    const labelA = labelMap?.[a] || a;
    const labelB = labelMap?.[b] || b;
    return labelA.localeCompare(labelB);
  });
}